import React, { useEffect, useRef, useState } from "react";
import type { FileRecord } from "../../services/filesService";
import { fetchThumbnailObjectUrl } from "../../data/thumbnail";
import { relativeTime } from "./relativeTime";

interface Props {
  file: FileRecord;
  /**
   * The signed-in user's access token. My Files cards are an owner-only
   * surface (private files included), and an `<img src>` can't send an
   * Authorization header — so the thumbnail is fetched as a blob with this
   * token and turned into an object URL (see `data/thumbnail.ts`) rather
   * than set directly as an `<img src>` URL. Without a token (auth not yet
   * resolved), the card just keeps showing the placeholder.
   */
  accessToken?: string;
  onOpen: (id: string) => void;
  onRename: (id: string, title: string) => void | Promise<void>;
  onDuplicate: (id: string) => void | Promise<void>;
  onDeleteRequest: (file: FileRecord) => void;
}

/**
 * A single "My Files" grid card: thumbnail placeholder, title (inline
 * rename), relative updated time, and a kebab menu (rename / duplicate /
 * delete). Delete never happens directly from here — it hands the file back
 * to the parent, which owns the confirm step.
 */
export const FileCard: React.FC<Props> = ({
  file,
  accessToken,
  onOpen,
  onRename,
  onDuplicate,
  onDeleteRequest,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  // Busy flag for Duplicate: a double-fire (double-click, or re-opening the
  // menu while the request is in flight) would create two copies.
  const [duplicating, setDuplicating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(file.title);
  const inputRef = useRef<HTMLInputElement>(null);
  const [thumbnailSrc, setThumbnailSrc] = useState<string | null>(null);
  const menuWrapRef = useRef<HTMLDivElement>(null);

  // Dismiss the actions menu on an outside click. This used to be a full-screen
  // backdrop <div> painted under the menu, but it covered the lower menu items
  // and swallowed their clicks — Delete and Duplicate silently did nothing. A
  // listener has no stacking context to get wrong.
  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!menuWrapRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  // Load the thumbnail as an object URL (owner-only surface — see the
  // `accessToken` doc comment above for why this isn't a plain `<img src>`).
  // `file.updated_at` is the cache-buster: a fresh save changes it, so
  // editing a file refreshes its thumbnail on the next My Files visit.
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setThumbnailSrc(null);
    if (!accessToken) {
      return;
    }
    fetchThumbnailObjectUrl(file.id, file.updated_at, accessToken).then(
      (url) => {
        if (!url) {
          return;
        }
        if (cancelled) {
          // The effect was cleaned up (unmount or dep change) before this
          // resolved — the object URL was still created and must be
          // revoked here, or it leaks for the life of the page.
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setThumbnailSrc(url);
      },
    );
    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [file.id, file.updated_at, accessToken]);

  useEffect(() => {
    if (!editing) {
      setDraftTitle(file.title);
    }
  }, [file.title, editing]);

  const startRename = () => {
    setDraftTitle(file.title);
    setEditing(true);
    setMenuOpen(false);
  };

  const commitRename = () => {
    const trimmed = draftTitle.trim();
    setEditing(false);
    if (!trimmed || trimmed === file.title) {
      setDraftTitle(file.title);
      return;
    }
    onRename(file.id, trimmed);
  };

  const cancelRename = () => {
    setDraftTitle(file.title);
    setEditing(false);
  };

  const handleDuplicate = async () => {
    if (duplicating) {
      return;
    }
    setDuplicating(true);
    setMenuOpen(false);
    try {
      await onDuplicate(file.id);
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <div
      data-testid="file-card"
      style={{
        display: "flex",
        flexDirection: "column",
        border: "1px solid var(--mf-border)",
        borderRadius: "0.75rem",
        // `hidden` rounds off the thumbnail — but it also CLIPS the actions
        // menu, which hangs below the card. That clipping is what made Delete
        // and Duplicate unreachable (they rendered outside the card box and
        // were cut off / covered by the next card in the grid). While the menu
        // is open, let it escape, and lift this card above its siblings.
        overflow: menuOpen ? "visible" : "hidden",
        zIndex: menuOpen ? 30 : undefined,
        background: "var(--mf-surface)",
        boxShadow: "var(--mf-shadow)",
        position: "relative",
        padding: "0.75rem",
        gap: "0.75rem",
      }}
    >
      <button
        type="button"
        data-testid={`file-card-open-${file.id}`}
        onClick={() => onOpen(file.id)}
        aria-label={`Open ${file.title}`}
        style={{
          display: "block",
          width: "100%",
          aspectRatio: "8 / 5",
          border: "none",
          padding: 0,
          cursor: "pointer",
          background: "var(--mf-thumb-bg)",
          borderRadius: "0.5rem",
          overflow: "hidden",
        }}
      >
        {thumbnailSrc ? (
          <img
            src={thumbnailSrc}
            alt={`Thumbnail for ${file.title}`}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <div
            data-testid="file-thumbnail-placeholder"
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--mf-thumb-icon)",
            }}
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              aria-hidden="true"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
          </div>
        )}
      </button>

      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "0.5rem",
          padding: "0 0.1rem 0.1rem",
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          {editing ? (
            <input
              ref={inputRef}
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitRename();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancelRename();
                }
              }}
              aria-label="File title"
              style={{
                width: "100%",
                fontSize: "0.95rem",
                fontWeight: 600,
                border: "1.5px solid var(--mf-accent)",
                background: "var(--mf-control-bg)",
                color: "var(--mf-text)",
                borderRadius: "0.25rem",
                padding: "0.15rem 0.35rem",
              }}
            />
          ) : (
            <div
              title={file.title}
              style={{
                fontSize: "0.95rem",
                fontWeight: 600,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {file.title}
            </div>
          )}
          <div style={{ fontSize: "0.8rem", color: "var(--mf-text-muted)" }}>
            Updated {relativeTime(file.updated_at)}
          </div>
        </div>

        {/*
          zIndex lifts the open menu above the *following* cards in the grid,
          which would otherwise paint over it (the menu hangs below this card).
        */}
        <div
          ref={menuWrapRef}
          style={{ position: "relative", zIndex: menuOpen ? 20 : undefined }}
        >
          <button
            type="button"
            aria-label="File actions"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: "0.25rem",
              borderRadius: "0.25rem",
              color: "var(--mf-text-muted)",
              lineHeight: 0,
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <circle cx="12" cy="5" r="1.5" />
              <circle cx="12" cy="12" r="1.5" />
              <circle cx="12" cy="19" r="1.5" />
            </svg>
          </button>

          {menuOpen && (
            <>
              <div
                role="menu"
                style={{
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 0.25rem)",
                  zIndex: 10,
                  minWidth: "9rem",
                  background: "var(--mf-surface)",
                  border: "1px solid var(--mf-border)",
                  borderRadius: "0.5rem",
                  boxShadow: "var(--mf-shadow-lg)",
                  overflow: "hidden",
                }}
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={startRename}
                  style={menuItemStyle}
                >
                  Rename
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled={duplicating}
                  onClick={handleDuplicate}
                  style={menuItemStyle}
                >
                  {duplicating ? "Duplicating…" : "Duplicate"}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onDeleteRequest(file);
                  }}
                  style={{ ...menuItemStyle, color: "var(--mf-danger)" }}
                >
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const menuItemStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  textAlign: "left",
  padding: "0.5rem 0.85rem",
  border: "none",
  background: "transparent",
  color: "var(--mf-text)",
  cursor: "pointer",
  fontSize: "0.9rem",
};

export default FileCard;
